/**
 * frozt-vault-integration — Maps Vultisig vault shares to frost-zm
 * key bundles for threshold-signed Sapling transactions.
 *
 * Key storage: bundles are kept ONLY in-memory for the current session
 * (see `_saplingBundles` below). The user persists them by downloading
 * a `.wult` file — never via localStorage, where the May 2026 audit
 * uncovered a long-standing leak (plain `.vult` files appearing to
 * have shielded shares because of an old DKG cache).
 *
 * Flow for a threshold Sapling send:
 *   1. Check if vault has a frozt KeyShareBundle → if not, run DKG first
 *   2. Scan Sapling notes using the bundle's DFVK
 *   3. Build a Sapling transaction (WasmTxBuilder)
 *   4. Extract sighash + alphas
 *   5. Run FroztSignSession over the relay (mobile co-signs)
 *   6. Complete the transaction with the threshold signature
 *   7. Broadcast
 */

import {
	initFroztWasm,
	isFroztReady,
	unpackBundle,
	deriveSaplingAddress,
	deriveSaplingDfvk,
	startSignSession,
	startDkgSession,
	startKeyImportSession,
	type FroztKeyBundle,
} from './froztBridge';

import { runCeremonyOverRelay, type CeremonyOptions } from './froztCeremonyRunner';

const LEGACY_STORAGE_PREFIX = 'frozt-sapling-bundle:';

/** In-memory bundle map — replaces the legacy `localStorage` cache. */
const _saplingBundles = new Map<string, Uint8Array>();

/**
 * Sweep legacy localStorage entries written by older builds so a
 * post-upgrade browser can't keep reporting "this vault already has
 * Sapling shares" off stale data. Idempotent.
 */
let _legacySweepDone = false;
export function __resetSaplingSweepGuardForTests(): void {
	_legacySweepDone = false;
}
export function sweepLegacySaplingLocalStorage(): void {
	if (_legacySweepDone) return;
	_legacySweepDone = true;
	if (typeof localStorage === 'undefined') return;
	try {
		const doomed: string[] = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (!key) continue;
			if (key.startsWith(LEGACY_STORAGE_PREFIX)) doomed.push(key);
		}
		for (const key of doomed) {
			try { localStorage.removeItem(key); } catch { /* */ }
		}
		if (doomed.length > 0) {
			console.info('[frozt-sapling] swept %d legacy localStorage entries',
				doomed.length);
		}
	} catch (err) {
		console.warn('[frozt-sapling] legacy localStorage sweep failed:',
			(err as Error).message);
	}
}

/** Store a frozt KeyShareBundle for a vault (in-memory only). */
export function storeSaplingBundle(vaultUid: string, bundle: Uint8Array): void {
	if (!vaultUid) return;
	sweepLegacySaplingLocalStorage();
	_saplingBundles.set(vaultUid, new Uint8Array(bundle));
}

/** Load a stored frozt KeyShareBundle for a vault (returns a copy). */
export function loadSaplingBundle(vaultUid: string): Uint8Array | null {
	if (!vaultUid) return null;
	const cached = _saplingBundles.get(vaultUid);
	if (!cached) return null;
	return new Uint8Array(cached);
}

/** Check if a vault has a frozt Sapling key bundle in the current session. */
export function hasSaplingBundle(vaultUid: string): boolean {
	if (!vaultUid) return false;
	return _saplingBundles.has(vaultUid);
}

/** Drop all session-scoped Sapling bundles (logout / switch vault). */
export function clearAllSaplingBundles(): void {
	_saplingBundles.clear();
	sweepLegacySaplingLocalStorage();
}

/**
 * Get the Sapling z-address for a vault (requires stored bundle).
 */
export async function getVaultSaplingAddress(vaultUid: string): Promise<string | null> {
	await initFroztWasm();
	const raw = loadSaplingBundle(vaultUid);
	if (!raw) return null;
	const bundle = unpackBundle(raw);
	return deriveSaplingAddress(bundle);
}

/**
 * Run a DKG ceremony to create new Sapling key shares for a vault.
 * Both this device and the mobile co-signer participate.
 *
 * @param vaultUid — Unique vault identifier
 * @param partyNames — All party names (e.g., ['desktop-share', 'mobile-share'])
 * @param myPartyName — This device's party name
 * @param birthday — Zcash birthday height for scanning
 * @param threshold — Minimum signers required (e.g., 2)
 * @param options — Relay/ceremony options
 * @returns The Sapling z-address
 */
export async function runSaplingDkg(
	vaultUid: string,
	partyNames: string[],
	myPartyName: string,
	birthday: number,
	threshold: number,
	options: CeremonyOptions,
): Promise<string> {
	await initFroztWasm();

	const dkgState = startDkgSession(
		partyNames.length,
		threshold,
		partyNames,
		myPartyName,
		birthday,
	);

	options.onProgress?.('Running Sapling DKG...');
	const bundleBytes = await runCeremonyOverRelay(dkgState.session, myPartyName, options);

	storeSaplingBundle(vaultUid, bundleBytes);
	const bundle = unpackBundle(bundleBytes);
	const address = deriveSaplingAddress(bundle);

	options.onProgress?.(`Sapling address: ${address}`);
	return address;
}

/**
 * Import an existing Zcash spending key (from seed/phrase) into the
 * FROST threshold scheme. Splits the key so that `threshold` parties
 * must cooperate to sign.
 */
export async function runSaplingKeyImport(
	vaultUid: string,
	partyNames: string[],
	myPartyName: string,
	birthday: number,
	threshold: number,
	seed: Uint8Array,
	accountIndex: number,
	options: CeremonyOptions,
): Promise<string> {
	await initFroztWasm();

	const sorted = [...partyNames].sort();
	const myIndex = sorted.indexOf(myPartyName);
	if (myIndex < 0) throw new Error('myPartyName not found in partyNames');

	const importState = startKeyImportSession(
		partyNames.length,
		threshold,
		partyNames,
		myPartyName,
		birthday,
		myIndex + 1,
		seed,
		accountIndex,
	);

	options.onProgress?.('Importing key into FROST scheme...');
	const bundleBytes = await runCeremonyOverRelay(importState.session, myPartyName, options);

	storeSaplingBundle(vaultUid, bundleBytes);
	const bundle = unpackBundle(bundleBytes);
	const address = deriveSaplingAddress(bundle);

	options.onProgress?.(`Sapling address: ${address}`);
	return address;
}

export interface ThresholdSaplingSignRequest {
	vaultUid: string;
	sighash: Uint8Array;
	alpha?: Uint8Array;
	partyNames: string[];
	myPartyName: string;
	ceremonyOptions: CeremonyOptions;
}

/**
 * Run a threshold signing ceremony for a Sapling spend authorisation.
 * Returns the FROST-aggregated RedJubjub signature.
 */
export async function thresholdSaplingSign(
	request: ThresholdSaplingSignRequest,
): Promise<Uint8Array> {
	await initFroztWasm();

	const raw = loadSaplingBundle(request.vaultUid);
	if (!raw) throw new Error('No Sapling key bundle for this vault. Run DKG first.');

	const bundle = unpackBundle(raw);
	const signState = startSignSession(
		request.sighash,
		request.partyNames,
		request.myPartyName,
		bundle,
		request.alpha,
	);

	request.ceremonyOptions.onProgress?.('Waiting for mobile co-sign...');
	return runCeremonyOverRelay(signState.session, request.myPartyName, request.ceremonyOptions);
}

/**
 * Check if a vault can perform threshold Sapling transactions.
 */
export function canDoThresholdSapling(vaultUid: string): boolean {
	return isFroztReady() && hasSaplingBundle(vaultUid);
}
